<script setup>
import { reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
import IntlTelInput from 'intl-tel-input/vueWithUtils';
import 'intl-tel-input/styles';
import TextInput from '@/Components/Input/TextInput.vue';
import InputLabel from '@/Components/Input/InputLabel.vue';
import PrimaryButton from '@/Components/Button/PrimaryButton.vue';
import Modal from '@/Components/Modal/Modal.vue';
import IconCheckCircle from '@/Components/Icons/IconCheckCircle.vue';
import { downloadReport } from '@/Common/DownloadReport.js';
import {
  TOOL_DOMAIN_SCORE,
  captureReportDownloaded,
  captureReportRequested,
  identifyFromEmailReport
} from '@/Common/Posthog.js';

const emailDomainFromAddress = (email) => {
  const domain = String(email ?? '')
    .trim()
    .toLowerCase()
    .split('@')[1];
  return domain || undefined;
};

const props = defineProps({
  domain: {
    type: String,
    required: true
  },
  defaultCountry: String
});

const emit = defineEmits(['close']);

const form = reactive({
  firstName: '',
  lastName: '',
  contactNumber: '',
  email: '',
  errors: {},
  processing: false
});

const state = reactive({ submitted: false });
const close = () => {
  emit('close');
};

const isValidNumber = ref(false);
const numberErrorCode = ref(0);
const telephoneInput = ref();

watch(
  () => form.contactNumber,
  () => {
    telephoneInput.value.instance.telInput.setCustomValidity('');
    telephoneInput.value.instance.telInput.reportValidity();
  }
);

const submitForm = () => {
  if (!isValidNumber.value) {
    telephoneInput.value.instance.telInput.setCustomValidity(
      t('domain_score.modals.email_report_modal.enter_valid_telephone')
    );
    telephoneInput.value.instance.telInput.reportValidity();
    return;
  }
  axios
    .post(route('score.email.report', props.domain), form)
    .then(() => {
      state.submitted = true;
      identifyFromEmailReport(form.email);
      captureReportRequested({
        step: 'submitted',
        tool: TOOL_DOMAIN_SCORE,
        testedDomain: props.domain,
        emailDomain: emailDomainFromAddress(form.email)
      });
    })
    .finally(() => (form.processing = false));
};

const download = () => {
  void downloadReport({
    url: route('score.export', [props.domain]),
    filename: `${t('domain_score.messages.report_filename', { domain: props.domain })}.pdf`
  }).then((downloadStarted) => {
    if (downloadStarted) {
      captureReportDownloaded({
        tool: TOOL_DOMAIN_SCORE,
        testedDomain: props.domain
      });
      close();
    }
  });
};
</script>

<template>
  <Modal :show="true" @close="close" max-width="lg">
    <template #title>
      {{ $t('domain_score.modals.email_report_modal.title') }}
    </template>
    <div v-if="!state.submitted">
      <p class="pb-8">
        {{ $t('domain_score.modals.email_report_modal.description') }}
      </p>

      <!-- Form -->
      <form @submit.prevent="submitForm" class="flex flex-col gap-4">
        <TextInput
          v-model.trim="form.firstName"
          :label="$t('domain_score.modals.email_report_modal.firstname')"
          :placeholder="
            $t('domain_score.modals.email_report_modal.enter_firstname')
          "
          required
          :error="
            $t('domain_score.modals.email_report_modal.firstname_required')
          "
        />
        <TextInput
          v-model.trim="form.lastName"
          :label="$t('domain_score.modals.email_report_modal.lastname')"
          :placeholder="
            $t('domain_score.modals.email_report_modal.enter_lastname')
          "
          required
        />
        <div class="w-full">
          <input-label
            :value="$t('domain_score.modals.email_report_modal.contact_num')"
          />
          <IntlTelInput
            ref="telephoneInput"
            class="border-white-600 bg-navy-100 hover:border-navy-200 flex !w-full rounded-lg px-5 py-2 leading-5 placeholder-gray-400 focus:bg-white focus:ring-0"
            @change-number="form.contactNumber = $event"
            @change-validity="isValidNumber = $event"
            @change-error-code="numberErrorCode = $event"
            :options="{
              initialCountry: defaultCountry
            }"
            required
          />
        </div>
        <TextInput
          type="email"
          v-model.trim="form.email"
          :label="$t('domain_score.modals.email_report_modal.email_address')"
          :placeholder="
            $t('domain_score.modals.email_report_modal.enter_email_address')
          "
          required
        />

        <!-- Buttons -->
        <div class="flex">
          <primary-button type="submit" :disabled="form.processing">
            {{ $t('domain_score.modals.email_report_modal.send_report') }}
          </primary-button>
        </div>
      </form>
    </div>
    <div v-else>
      <div class="mb-6 flex justify-between rounded-lg bg-green-50 p-5">
        <IconCheckCircle :size="50" class="mr-2 shrink-0 text-green-600" />
        <div class="text-green-700">
          <h2 class="font-semibold">
            {{ $t('domain_score.modals.email_report_modal.report_sent') }}
          </h2>
          {{
            $t('domain_score.modals.email_report_modal.report_sent_description')
          }}
        </div>
      </div>
      <PrimaryButton
        @click="download"
        datadog-action-name="Download Report - modal"
      >
        {{ $t('domain_score.modals.email_report_modal.download_report') }}
      </PrimaryButton>
    </div>
  </Modal>
</template>
